import { useParams, Navigate, Link } from 'react-router-dom'
import { getModuleBySlug, MODULE_THEME } from '@/data/modules'
import type { ModulePoint, ModuleTheme } from '@/data/modules'
import { GradientHero } from '@/components/layout'
import { Button, SignupCta, TestimonialCard } from '@/components/ui'
import { ModuleTierBadge } from '@/components/pricing'
import { SEO } from '@/components/seo'
import ModuleIcon from './ModuleIcon'

function PointCard({ point, theme }: { point: ModulePoint; theme: ModuleTheme }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card-hover">
      <div
        className={`w-12 h-12 rounded-xl ${theme.iconBg} ${theme.accentText} flex items-center justify-center mb-5`}
      >
        <ModuleIcon name={point.icon} className="w-6 h-6" />
      </div>
      <h3 className="font-serif text-xl font-medium text-slate-900 mb-2">{point.title}</h3>
      <p className="text-[15px] text-slate-600 leading-relaxed">{point.body}</p>
    </div>
  )
}

function SectionHeading({ label, title, theme }: { label: string; title: string; theme: ModuleTheme }) {
  return (
    <div className="text-center mb-12">
      <p className={`text-xs font-semibold ${theme.accentText} tracking-[0.15em] uppercase mb-3`}>
        {label}
      </p>
      <h2 className="font-serif text-[clamp(28px,3.5vw,38px)] font-normal tracking-[-0.02em] text-slate-900">
        {title}
      </h2>
    </div>
  )
}

export default function ProductModulePage() {
  const { slug } = useParams<{ slug: string }>()
  const module = slug ? getModuleBySlug(slug) : undefined

  if (!module) {
    return <Navigate to="/product" replace />
  }

  const theme = MODULE_THEME[module.colorClass]

  return (
    <>
      <SEO
        title={module.seo.title}
        description={module.seo.description}
        keywords={module.seo.keywords}
        url={`/product/${module.slug}`}
      />

      {/* Hero */}
      <GradientHero
        background={theme.heroBackground}
        eyebrow={module.hero.eyebrow}
        title={
          <>
            {module.hero.title}{' '}
            <span className={theme.heroAccent}>{module.hero.titleAccent}</span>
          </>
        }
        subtitle={module.hero.subtitle}
        actions={
          <div className="flex flex-wrap gap-4 justify-center">
            <SignupCta location="product_cta" className={theme.ctaButton}>
              Start Free Trial
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </SignupCta>
            <Button
              href="/pricing"
              variant="secondary"
              size="lg"
              className="!bg-white/10 !text-white !border-white/40 backdrop-blur-sm hover:!bg-white/20 hover:!border-white/60"
            >
              View Pricing
            </Button>
          </div>
        }
        meta={
          <ModuleTierBadge
            pricingModules={module.pricingModules}
            variant="hero"
            colorClass={module.colorClass}
          />
        }
      />

      {/* Use cases */}
      {module.useCases && module.useCases.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-[1100px] mx-auto">
            <SectionHeading
              label="Where you'd use it"
              title="Built for the way you work"
              theme={theme}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {module.useCases.map((point, i) => (
                <PointCard key={i} point={point} theme={theme} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {module.benefits && module.benefits.length > 0 && (
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-[1100px] mx-auto">
            <SectionHeading
              label="What you get"
              title={`Why advisers love ${module.name}`}
              theme={theme}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {module.benefits.map((point, i) => (
                <PointCard key={i} point={point} theme={theme} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      {module.stats && module.stats.length > 0 && (
        <section className="py-16 px-6">
          <div className={`max-w-[900px] mx-auto border rounded-3xl px-8 py-12 ${theme.statCard}`}>
            <div className="flex flex-col sm:flex-row justify-center gap-12 text-center">
              {module.stats.map((stat, i) => (
                <div key={i}>
                  <div className={`font-serif text-[clamp(32px,4vw,44px)] font-light ${theme.accentText}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {module.testimonial && (
        <section className="py-16 px-6">
          <div className="max-w-[680px] mx-auto">
            <TestimonialCard
              quote={module.testimonial.quote}
              author={module.testimonial.author}
              role={module.testimonial.role}
              company={module.testimonial.company}
            />
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-[900px] mx-auto bg-white border border-slate-200 rounded-3xl py-16 px-12 text-center shadow-card">
          <h2 className="font-serif text-[clamp(30px,4vw,42px)] font-normal tracking-[-0.02em] text-slate-900 mb-4">
            {module.cta ? (
              <>
                {module.cta.title}{' '}
                <span className={theme.accentText}>{module.cta.titleAccent}</span>
              </>
            ) : (
              <>
                Try <span className={theme.accentText}>{module.name}</span> free
              </>
            )}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-[520px] mx-auto mb-6">
            {module.cta?.subtitle ?? module.tagline}
          </p>
          <div className="mb-5">
            <ModuleTierBadge
              pricingModules={module.pricingModules}
              variant="card"
              colorClass={module.colorClass}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <SignupCta location="product_cta" className={theme.ctaButton}>
              Start Free Trial
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </SignupCta>
            <Button href="/pricing" variant="secondary" size="lg">
              View Pricing
            </Button>
          </div>
          {module.cta?.note && <p className="text-sm text-slate-400">{module.cta.note}</p>}
        </div>
      </section>

      {/* Back to hub */}
      <div className="text-center px-6 pb-16">
        <Link
          to="/product"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          All modules
        </Link>
      </div>
    </>
  )
}
